// Owns the linked device for the session: restores it from IndexedDB on load, opens a
// device session and the relay socket once the API says the device is active, waits (by
// polling) while it is pending approval, and tears everything down on unlink.
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { setSessionToken } from "../api/client";
import { messages } from "../i18n";
import { loadProto, type Proto } from "../proto/index";
import { RelayClient, type RelayClientOptions } from "../relay/client";
import { protoCodec } from "../relay/codec";
import { DeviceContext, type DeviceContextValue, type DeviceState } from "./context";
import { deviceFingerprint } from "./fingerprint";
import { AUTH_ALG } from "./keys";
import { deviceStatus, enrolBrowser, type LinkedDevice, openSession, restoreLinkedDevice } from "./link";
import { forgetLinkedDevice } from "./store";

export interface DeviceProviderProps {
  children: ReactNode;
  /** Injected by tests; the default is the browser WebSocket to /rtc/client. */
  relayOptions?: Partial<Omit<RelayClientOptions, "codec" | "sessionToken">>;
  /** How often a pending browser asks whether it was approved. */
  pendingPollMs?: number;
}

function message(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function DeviceProvider({ children, relayOptions, pendingPollMs = 2000 }: DeviceProviderProps) {
  const [state, setState] = useState<DeviceState>({ kind: "loading" });
  const relayRef = useRef<RelayClient | null>(null);

  const bringUp = useCallback(
    async (device: LinkedDevice): Promise<void> => {
      const proto: Proto = await loadProto();
      const status = await deviceStatus(device.deviceId);
      if (status === "pending") {
        const fingerprint = deviceFingerprint(proto, AUTH_ALG, device.keys.authPublicKey);
        return setState({ kind: "unlinked", pending: { device, fingerprint } });
      }
      if (status === "revoked") {
        await forgetLinkedDevice();
        return setState({ kind: "unlinked", error: messages().devices.browser.rejected });
      }
      const session = await openSession(device);
      relayRef.current?.close();
      const relay = new RelayClient({ ...relayOptions, codec: protoCodec(proto), sessionToken: session.token });
      relay.connect();
      relayRef.current = relay;
      setState({ kind: "linked", device, session, relay, proto });
    },
    [relayOptions],
  );

  useEffect(() => {
    let cancelled = false;
    restoreLinkedDevice()
      .then(async (device) => {
        if (cancelled) return;
        if (!device) return setState({ kind: "unlinked" });
        await bringUp(device);
      })
      .catch((e: unknown) => {
        if (!cancelled) setState({ kind: "unlinked", error: message(e) });
      });
    return () => {
      cancelled = true;
      relayRef.current?.close();
      relayRef.current = null;
    };
  }, [bringUp]);

  // While pending, ask until an active device approves (or rejects) this browser.
  const pending = state.kind === "unlinked" ? state.pending?.device : undefined;
  useEffect(() => {
    if (!pending) return;
    const timer = setInterval(() => {
      bringUp(pending).catch((e: unknown) => setState({ kind: "unlinked", error: message(e) }));
    }, pendingPollMs);
    return () => clearInterval(timer);
  }, [pending, pendingPollMs, bringUp]);

  const link = useCallback(
    async (name: string, bootstrapCode?: string) => {
      try {
        await bringUp(await enrolBrowser(name, bootstrapCode));
      } catch (e) {
        setState({ kind: "unlinked", error: message(e) });
        throw e;
      }
    },
    [bringUp],
  );

  const unlink = useCallback(async () => {
    relayRef.current?.close();
    relayRef.current = null;
    setSessionToken(undefined);
    await forgetLinkedDevice();
    setState({ kind: "unlinked" });
  }, []);

  const value = useMemo<DeviceContextValue>(() => ({ ...state, link, unlink }), [state, link, unlink]);
  return <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>;
}
